import { ExternalLink } from '@components/ExternalLink'
import Coupon from '@components/membership/coupon/Coupon'
import { LiteMemberAvatar } from '@components/membership/LiteMemberAvatar'
import { LitePlanTag } from '@components/membership/LitePlanTag'
import { TransactionStatusTag } from '@components/membership/transaction/StatusTag'
import SBNiceDate from '@components/utils/SBNiceDate'
import { useAppSelector } from '@store/hooks'
import {
  selectMembershipTransactionById,
  selectSelectedMembershipTransaction,
} from '@store/membership/MembershipTransactionReducer'
import { EllipsisMiddle } from '@supplement-bacon/react-toolkit/components'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { formatSnakeCaseLabel } from '@utils/string'
import { Descriptions, Flex, Space, Spin, Tooltip, Typography } from 'antd'
import { useSearchParams } from 'react-router-dom'

const { Text } = Typography

/**
 * SBRM "View" content for a transaction (ticket 537, API #298). Reads the
 * selected row straight from `MembershipTransactionReducer` - the row is
 * already loaded by whichever table/preview opened the drawer, so no extra
 * fetch is needed. Guarded with a spinner in case the slice is empty (e.g.
 * the overview preview, which self-fetches on a shorter list).
 */
const View = () => {
  // SBRM skips `setSelected` for our UUID v7 ids (its guard only accepts an
  // integer, a ULID or a UUID v1-v5), so fall back to the `entityId` it does
  // put in the URL.
  const [searchParams] = useSearchParams()
  const selected = useAppSelector(selectSelectedMembershipTransaction())
  const fromUrl = useAppSelector(
    selectMembershipTransactionById(searchParams.get('entityId'))
  )
  const item = selected ?? fromUrl

  if (!item) {
    return <Spin />
  }

  const hasDiscount =
    item.listPrice != null && item.listPrice !== item.netAmount

  return (
    // `marginTop`: the modal title sits right on top of the member row otherwise.
    <Flex vertical gap={16} style={{ marginTop: 16 }}>
      <Flex align="center" justify="space-between">
        <LiteMemberAvatar member={item.member} />
        <LitePlanTag plan={item.plan} />
      </Flex>

      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Status">
          <TransactionStatusTag status={item.status} />
        </Descriptions.Item>

        <Descriptions.Item label="PSP Reference">
          <Space>
            <Text strong copyable={{ text: item.reference }}>
              {item.reference}
            </Text>
            {item.pspUrl && (
              <Tooltip title="See payment within your Payment Service Provider">
                <ExternalLink to={item.pspUrl} content="" />
              </Tooltip>
            )}
          </Space>
        </Descriptions.Item>

        <Descriptions.Item label="Net amount">
          <Text strong>
            {analyticsCurrency(item.netAmount, true, item.plan?.currency)}
          </Text>
        </Descriptions.Item>

        {hasDiscount && (
          <Descriptions.Item label="List price">
            <Text delete>
              {analyticsCurrency(
                item.listPrice ?? undefined,
                true,
                item.plan?.currency
              )}
            </Text>
          </Descriptions.Item>
        )}

        <Descriptions.Item label="Coupon">
          {item.coupon ? (
            <Coupon.Avatar showDetails model={item.coupon} />
          ) : (
            '--'
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Payment method">
          {item.paymentMethod ? formatSnakeCaseLabel(item.paymentMethod) : '--'}
        </Descriptions.Item>

        <Descriptions.Item label="Created at">
          <SBNiceDate date={item.date ?? undefined} />
        </Descriptions.Item>

        <Descriptions.Item label="Raw amount (cents)">
          <Text>{item.netAmount}</Text>
        </Descriptions.Item>

        <Descriptions.Item label="Plethore ID">
          <EllipsisMiddle suffixCount={5} copyable={{ text: item.id }}>
            {item.id}
          </EllipsisMiddle>
        </Descriptions.Item>
      </Descriptions>
    </Flex>
  )
}

export default View
